import { claimflowApiClient } from './claimflowApiClient';

/**
 * Claim search ("Rechercher un dossier", mahhp 22861).
 *
 * One call to origami's generic ERP proxy, slug
 * `boby-ws2-gs-sel-recherche-sin` → VEOS `GET /boBy/v2/WS2_GS_SEL_RECHERCHE_SIN`.
 * `filtre` is free text (internal claim number, company reference, plate or
 * client name); the proxy wraps the VEOS list in its `ErpProxyResponse`
 * envelope (`{ success, data: { total, data: rows } }`), like every other
 * claimflow list endpoint (see `documentsService`).
 *
 * `ID_SIN` is what the claim screen URL needs (`/task?id_sin=…`). The
 * documented VEOS response does not return it yet (open point of the ticket):
 * a row without a usable `ID_SIN` maps to `idSin: null` and the search modal
 * renders it disabled.
 */

interface ErpProxyResponse<T> {
  success: boolean;
  http_status?: number;
  data: T;
  error?: { code: string; message: string } | null;
}

type ErpListResponse<T> = ErpProxyResponse<{
  total?: number;
  // VEOS may omit `data` on an empty result.
  data?: T[] | null;
}>;

/** A raw `WS2_GS_SEL_RECHERCHE_SIN` row. */
export interface VeosClaimSearchRow {
  /** Claim id — expected, but not returned by VEOS yet (may be absent). */
  ID_SIN?: number | string | null;
  /** Internal claim number (e.g. "25700751"). */
  NCBT_SIN?: string | null;
  /** Company (insurer) reference — often empty. */
  NCIE_SIN?: string | null;
  /** Client name. */
  NOM2_PER?: string | null;
}

/** A claim search result, as the search modal renders it. */
export interface ClaimSearchResult {
  /** `null` when VEOS did not return a usable `ID_SIN` (row not openable). */
  idSin: number | null;
  ncbtSin: string;
  /** `null` when VEOS returned no company reference (null / blank). */
  ncieSin: string | null;
  nom2Per: string;
}

export const CLAIM_SEARCH_ENDPOINT =
  '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-recherche-sin';

function unwrap<T>(envelope: ErpProxyResponse<T>): T {
  if (!envelope.success) {
    throw new Error(envelope.error?.message ?? 'ERP request failed');
  }
  return envelope.data;
}

/** A positive integer claim id (number or numeric string), else `null`. */
function toIdSin(raw: VeosClaimSearchRow['ID_SIN']): number | null {
  if (raw == null || raw === '') return null;
  const value = Number(raw);
  return Number.isInteger(value) && value > 0 ? value : null;
}

function toText(raw: string | null | undefined): string {
  return (raw ?? '').trim();
}

export function mapClaimSearchRow(row: VeosClaimSearchRow): ClaimSearchResult {
  const ncieSin = toText(row.NCIE_SIN);
  return {
    idSin: toIdSin(row.ID_SIN),
    ncbtSin: toText(row.NCBT_SIN),
    ncieSin: ncieSin === '' ? null : ncieSin,
    nom2Per: toText(row.NOM2_PER),
  };
}

export const claimSearchService = {
  /**
   * Search claims matching `filtre`. Pass the `signal` of an `AbortController`
   * to cancel a stale typeahead request (axios rejects with a cancel error).
   *
   * `suppressErrorToast`: this is a typeahead — failures are shown inline in
   * the search modal, never as the global API error toast.
   */
  async searchClaims(filtre: string, signal?: AbortSignal): Promise<ClaimSearchResult[]> {
    const response = await claimflowApiClient.get<ErpListResponse<VeosClaimSearchRow>>(
      CLAIM_SEARCH_ENDPOINT,
      {
        // `perPage: 9999` overrides VEOS's default pagination (codebase convention).
        params: { filtre, perPage: 9999 },
        suppressErrorToast: true,
        ...(signal ? { signal } : {}),
      },
    );
    return (unwrap(response.data).data ?? []).map(mapClaimSearchRow);
  },
};
